import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, CheckCircle, Star, ArrowRight, Flame, Zap } from 'lucide-react';
import { ProductPricing } from '@/services/discount.service';
import { formatPrice } from '@/hooks/useFeaturedPricing';

/**
 * Props for UltimatePromptBundleCard component
 */
interface UltimatePromptBundleCardProps {
  /** Pricing data from API - if not provided, shows loading state */
  pricing?: ProductPricing | null;
  /** Whether pricing is currently loading */
  loading?: boolean;
}

/**
 * UltimatePromptBundleCard Component
 *
 * A compelling card component for displaying the Ultimate AI Prompt & Guide Bundle
 * digital product with professional styling, urgency elements, and call-to-action buttons.
 *
 * @component
 */
export default function UltimatePromptBundleCard({ pricing, loading = false }: UltimatePromptBundleCardProps) {
  // Extract pricing values with fallbacks
  const displayPrice = pricing?.display_price ?? 47;
  const originalPrice = pricing?.original_price ?? 197;
  const isSaleActive = pricing?.is_sale_active ?? true;
  const savingsAmount = pricing?.savings_amount ?? 150;
  const savingsPercentage = pricing?.savings_percentage ?? 76;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="group relative">
        {/* Mega Glow Effect - Amber/Orange theme */}
        <div className="absolute -inset-2 bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 rounded-3xl blur-3xl opacity-30 group-hover:opacity-50 transition-all duration-500 animate-pulse"></div>

        {/* Bundle Card */}
        <div className="relative bg-gradient-to-br from-white/[0.15] to-white/[0.05] backdrop-blur-2xl rounded-3xl border-2 border-amber-500/30 group-hover:border-amber-500/50 transition-all duration-500 overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-600/20 via-orange-600/20 to-red-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative p-8 md:p-12">
            {/* Top Section */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-10">
              {/* Left: Title & Description */}
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-amber-500/50 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <FileText className="w-10 h-10" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="bg-gradient-to-r from-amber-600/30 via-orange-600/30 to-red-600/30 border border-amber-500/50 px-4 py-2 rounded-full inline-block mb-2">
                      <span className="text-sm font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent flex items-center gap-2">
                        <Flame className="w-4 h-4 text-orange-400" />
                        DIGITAL DOWNLOAD
                      </span>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                      Ultimate AI Prompt Bundle
                    </h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 mb-4 leading-relaxed">
                  <span className="text-amber-400 font-semibold">1,400+ expertly crafted prompts</span> across 90 professional PDF guides.
                  Copy, paste, and get instant results with ChatGPT, Claude, and more.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  Stop spending hours crafting prompts. Get professional-grade templates for business, marketing, coding, writing, and productivity - instantly downloadable and ready to use.
                </p>
              </div>

              {/* Right: Pricing */}
              <div className="lg:text-right">
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-8 border border-amber-500/30">
                  <div className="text-sm text-amber-400 uppercase tracking-wider mb-2 font-semibold">Instant Download</div>
                  {loading ? (
                    <div className="space-y-3">
                      <div className="h-16 w-32 bg-white/10 rounded animate-pulse mx-auto lg:ml-auto lg:mr-0"></div>
                      <div className="h-8 w-40 bg-white/10 rounded animate-pulse mx-auto lg:ml-auto lg:mr-0"></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-center lg:justify-end gap-3 mb-2">
                        <span className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                          {formatPrice(displayPrice)}
                        </span>
                      </div>
                      <div className="flex items-center justify-center lg:justify-end gap-2 mb-4">
                        {isSaleActive && originalPrice > displayPrice && (
                          <>
                            <span className="text-2xl text-gray-400 line-through font-semibold decoration-2">
                              {formatPrice(originalPrice)}
                            </span>
                            <span className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 px-3 py-1 rounded-full text-sm font-bold text-green-400">
                              SAVE {savingsPercentage}%
                            </span>
                          </>
                        )}
                      </div>
                      <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 px-4 py-2 rounded-full">
                        <Zap className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-amber-300">Lifetime Access</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* What's Included */}
            <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-amber-500/20 mb-8">
              <h4 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Download className="w-6 h-6 text-amber-400" />
                <span>What You'll Get Instantly</span>
              </h4>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300"><span className="text-white font-semibold">1,400+</span> Ready-to-Use Prompts</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300"><span className="text-white font-semibold">90</span> Professional PDF Guides</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300"><span className="text-white font-semibold">15</span> Categories Covered</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">LLMs, Images, Video, <span className="text-white font-semibold">Code & More</span></span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">Cheat Sheets & <span className="text-white font-semibold">Quick Starts</span></span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">Workflows & <span className="text-white font-semibold">Templates</span></span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">Works with <span className="text-white font-semibold">All AI Tools</span></span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300"><span className="text-white font-semibold">Free</span> Lifetime Updates</span>
                  </div>
                </div>
              </div>

              {/* Urgency Banner */}
              <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl p-4 flex items-center justify-center gap-3">
                <Flame className="w-5 h-5 text-red-400 animate-pulse" />
                <span className="text-sm font-semibold text-red-300">
                  Limited Time: Get {savingsPercentage}% OFF + All Future Updates FREE
                </span>
                <Flame className="w-5 h-5 text-red-400 animate-pulse" />
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/ultimate-prompt-bundle"
                className="flex-1 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-400 hover:via-orange-400 hover:to-red-400 py-5 rounded-xl font-bold text-xl flex items-center justify-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/50 group/btn"
              >
                <Download className="w-6 h-6" />
                <span>Get Instant Access</span>
                <ArrowRight className="w-6 h-6 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/ultimate-prompt-bundle"
                className="sm:w-48 bg-white/10 hover:bg-white/20 backdrop-blur-sm py-5 rounded-xl font-bold text-lg border border-white/20 hover:border-amber-500/40 transition-all text-center"
              >
                Learn More
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 mt-8 pt-8 border-t border-white/10">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Download className="w-5 h-5 text-amber-400" />
                <span>Instant Download</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Lifetime Access</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span>4.9/5 Rating</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
